import { useEffect, useState } from 'react';

import { ModalBase } from './ModalBase';

import type React from 'react';

export interface EncaminharTarget {
  userId: string;
  name: string;
  primaryRole?: string;
}

export interface EncaminharOutroModalProps {
  open: boolean;
  /** Atendentes reais do tenant (handoff targets) — NUNCA nomes hardcoded. */
  targets: EncaminharTarget[];
  isLoadingTargets: boolean;
  targetsError: boolean;
  isPending: boolean;
  failed: boolean;
  outcome: string | null;
  onClose: () => void;
  onConfirm: (args: { targetUserId: string; transferReason: string }) => void;
}

/** Piso de UI mais estrito que o backend (transfer exige ≥10) — coerente com a Tela 2. */
const REASON_FLOOR = 20;

/**
 * EncaminharOutroModal — converte modals-3.jsx:554 (EncaminharOutroModal). Encaminha
 * a conversa a outro atendente (`POST takeovers/:id/transfer {targetUserId, transferReason}`).
 * O picker é REAL (handoff targets do tenant); as options hardcoded do protótipo
 * (Pedro/Vitoria/…) NÃO foram portadas. Guard anti duplo-submit; desfecho da resposta.
 */
export function EncaminharOutroModal({
  open,
  targets,
  isLoadingTargets,
  targetsError,
  isPending,
  failed,
  outcome,
  onClose,
  onConfirm,
}: EncaminharOutroModalProps): React.JSX.Element | null {
  const [targetUserId, setTargetUserId] = useState('');
  const [reason, setReason] = useState('');

  // Default para o 1º alvo quando a lista real chega (sem chumbar id de exemplo).
  useEffect(() => {
    if (targetUserId === '' && targets.length > 0) {
      setTargetUserId(targets[0]?.userId ?? '');
    }
  }, [targets, targetUserId]);

  if (!open) return null;
  const trimmedLen = reason.trim().length;
  const canSubmit = targetUserId !== '' && trimmedLen >= REASON_FLOOR && !isPending;

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title="Encaminhar conversa"
      width={520}
      footer={
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button className="btn-ghost-mini" type="button" onClick={onClose} disabled={isPending}>
            Cancelar
          </button>
          <button
            className="btn-ghost-mini btn-ghost-mini--primary"
            type="button"
            disabled={!canSubmit}
            onClick={() => {
              onConfirm({ targetUserId, transferReason: reason });
            }}
          >
            {isPending ? 'Encaminhando…' : 'Encaminhar'}
          </button>
        </div>
      }
    >
      <div className="modal-form">
        <p style={{ fontSize: 'var(--text-sm)' }}>
          Encaminhar a conversa para outro atendente, em vez de devolver à original.
        </p>
        <label className="modal-form__field">
          <span className="modal-form__label">Atendente</span>
          {isLoadingTargets ? (
            <span style={{ color: 'var(--fg-3)', fontSize: 'var(--text-sm)' }}>
              Carregando atendentes…
            </span>
          ) : targetsError ? (
            <span className="modal-form__hint" role="alert">
              Não foi possível carregar a lista de atendentes.
            </span>
          ) : targets.length === 0 ? (
            <span className="modal-form__hint">Sem atendentes disponíveis para encaminhar.</span>
          ) : (
            <select
              className="modal-form__select"
              value={targetUserId}
              onChange={(e) => {
                setTargetUserId(e.target.value);
              }}
              aria-label="Atendente destino"
            >
              {targets.map((tgt) => (
                <option key={tgt.userId} value={tgt.userId}>
                  {tgt.name}
                  {tgt.primaryRole !== undefined ? ` · ${tgt.primaryRole}` : ''}
                </option>
              ))}
            </select>
          )}
        </label>
        <label className="modal-form__field">
          <span className="modal-form__label">
            Razão (obrigatória · mínimo {REASON_FLOOR} caracteres)
          </span>
          <textarea
            className="modal-form__textarea"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            rows={3}
            placeholder="O atendente destino tem expertise neste caso específico…"
            aria-label="Razão do encaminhamento"
          />
          <span className="modal-form__hint">
            {trimmedLen}/{REASON_FLOOR} mínimo
          </span>
        </label>
        {failed && (
          <div className="banner banner--danger" role="alert">
            <div className="banner__body">
              <span className="banner__title">Não foi possível encaminhar a conversa.</span>
            </div>
          </div>
        )}
        {outcome !== null && (
          <div className="modal-form__callout" role="status">
            {outcome}
          </div>
        )}
      </div>
    </ModalBase>
  );
}
